"use client";

import { useMemo } from "react";
import Link from "next/link";
import { type DeepReadonly } from "@/utils/tsmagic";
import WatermarkLogo from "public/images/logoEndcapTransparent.png";
import { AbsoluteFill, Img, Sequence } from "remotion";
import { type z } from "zod";

import { type CustomCardObjectSchema } from "../core/derivedSchema";
import { useCurrentFrameStyle } from "../core/useCurrentFrameStyle";
import { Bubbles } from "./customElements/Bubbles";
import { ConfettiPop } from "./customElements/ConfettiPop";
import { FlowerExplosion } from "./customElements/FlowerExplosion";
import { Scatter_OLD } from "./customElements/Scatter";
import { Snow } from "./customElements/Snow";
import { Trail } from "./customElements/Trail";

// If you add a value to the metaschema, update the customCardObjectEditor and add a editor function for the value

export type CustomCardObject = z.infer<typeof CustomCardObjectSchema>;
export type ReadonlyCustomCardObject = DeepReadonly<CustomCardObject>;

export const CustomCardObjectComponent: React.FC<{
  cardObject: ReadonlyCustomCardObject;
}> = ({ cardObject }) => {
  return (
    <Sequence
      from={cardObject.sequence.from}
      durationInFrames={cardObject.sequence.durationInFrames}
      layout="none"
    >
      <CustomCardObjectComponentInner cardObject={cardObject} />
    </Sequence>
  );
};

const CustomCardObjectComponentInner: React.FC<{
  cardObject: ReadonlyCustomCardObject;
}> = ({ cardObject }) => {
  const { outerStyle, innerStyle } = useCurrentFrameStyle(cardObject);

  return useMemo(() => {
    switch (cardObject.metadata.customObjectName) {
      case "FlowerExplosion":
        return (
          <div style={{ zIndex: cardObject.transform.zIndex }}>
            <FlowerExplosion />
          </div>
        );
      case "WaterMarkOverlay": {
        return (
          <div style={outerStyle}>
            {/* Explicit 32x32. innerStyle sets only translate/rotate/scale, so
                rendered size comes from the element's INTRINSIC size. The
                closed-box SVG this replaced declared width="32" height="32";
                this raster is 400x400, which would render every stored
                WaterMarkOverlay transform 12.5x too large. Pinning it back
                keeps existing cards pixel-identical - the mark changes, its
                geometry does not. */}
            <Img
              src={WatermarkLogo.src}
              style={{ ...innerStyle, width: 32, height: 32 }}
              key={cardObject.id}
            />
          </div>
        );
      }
      case "Scatter": {
        return <Scatter_OLD cardObject={cardObject} />;
      }
      case "Bubbles": {
        return <Bubbles cardObject={cardObject} />;
      }
      case "ConfettiPop": {
        return <ConfettiPop cardObject={cardObject} />;
      }
      case "Trail": {
        return <Trail cardObject={cardObject} />;
      }
      case "Snow": {
        return <Snow cardObject={cardObject} />;
      }
      case "Watermark": {
        return (
          <Link href="/?category=free" target={"_blank"} style={outerStyle}>
            <Img
              src={WatermarkLogo.src}
              style={innerStyle}
              key={cardObject.id}
            />
          </Link>
        );
      }
      default:
        return (
          <div className="h-full w-full bg-purple-400">
            <div style={outerStyle}>
              {/* Same 32x32 pin as WaterMarkOverlay: this fallback catches
                  unrecognised custom objects, whose stored transforms were
                  also authored against the 32x32 SVG. */}
              <Img
                src={WatermarkLogo.src}
                style={{ ...innerStyle, width: 32, height: 32 }}
                key={cardObject.id}
              />
            </div>
          </div>
        );
    }
  }, [cardObject, innerStyle, outerStyle]);
};
